import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";

export interface GuruThread {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  last_message_at: string;
}

export interface GuruMessage {
  id: string;
  role: "user" | "assistant" | "tool" | "system";
  content: string;
  parts: Array<{ type: string; data: any }> | null;
  created_at: string;
}

export const useGuruThreads = () => {
  const { user } = useAuth();
  const [threads, setThreads] = useState<GuruThread[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await supabase
      .from("guru_chat_threads" as any)
      .select("*")
      .eq("user_id", user.id)
      .order("updated_at", { ascending: false });
    setThreads((data ?? []) as any);
    setLoading(false);
  }, [user?.id]);

  useEffect(() => { void load(); }, [load]);

  const createThread = useCallback(async (): Promise<GuruThread | null> => {
    if (!user) return null;
    const { data } = await supabase
      .from("guru_chat_threads" as any)
      .insert({ user_id: user.id, title: "Nova conversa" } as any)
      .select("*")
      .single();
    if (data) {
      setThreads((prev) => [data as any, ...prev]);
    }
    return (data as any) ?? null;
  }, [user?.id]);

  const deleteThread = useCallback(async (id: string) => {
    await supabase.from("guru_chat_threads" as any).delete().eq("id", id);
    setThreads((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return { threads, loading, reload: load, createThread, deleteThread };
};

export const useGuruMessages = (threadId: string | null) => {
  const [messages, setMessages] = useState<GuruMessage[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!threadId) { setMessages([]); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from("guru_chat_messages" as any)
        .select("id, role, content, parts, created_at")
        .eq("thread_id", threadId)
        .order("created_at", { ascending: true });
      if (!cancelled) {
        setMessages((data ?? []) as any);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [threadId]);

  const appendLocal = useCallback((msg: GuruMessage) => {
    setMessages((prev) => [...prev, msg]);
  }, []);

  return { messages, loading, appendLocal, setMessages };
};

export const useGuruQuota = () => {
  const { user } = useAuth();
  const [used, setUsed] = useState(0);
  const [loading, setLoading] = useState(true);
  const isPremium = !!user?.isSubscribed;
  const dailyLimit = 1;

  const reload = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const today = new Date().toISOString().slice(0, 10);
    const { data } = await supabase
      .from("guru_chat_usage" as any)
      .select("count")
      .eq("user_id", user.id)
      .eq("day", today)
      .maybeSingle();
    setUsed(((data as any)?.count as number) ?? 0);
    setLoading(false);
  }, [user?.id, user?.isSubscribed]);

  useEffect(() => { void reload(); }, [reload]);

  return {
    isPremium,
    used,
    dailyLimit,
    remaining: isPremium ? Infinity : Math.max(0, dailyLimit - used),
    canSend: isPremium || used < dailyLimit,
    loading,
    reload,
  };
};

export async function sendGuruMessage(threadId: string, message: string, mode?: string) {
  const { data, error } = await supabase.functions.invoke("guru-chat", {
    body: { threadId, message, mode },
  });
  if (error) {
    // Supabase wraps non-2xx as FunctionsHttpError; try to extract status
    const ctx: any = (error as any).context;
    let status = 0;
    let payload: any = null;
    try {
      status = ctx?.status ?? 0;
      payload = ctx ? await ctx.json() : null;
    } catch {}
    throw { status, payload, message: error.message };
  }
  return data as { message: GuruMessage };
}
